// The sentence a non-admin reads when the traffic-light gate refuses a new
// appointment.
//
// There used to be ONE sentence for all three blocking lights: „bestehender
// Fall ist {{state}}. Erst nach Ablauf oder Absage." For a planned or a recent
// appointment that was roughly right. For „Auftrag bekommen" it named two
// things that never happen — a signed contract does not run out and is not
// cancelled — and it never said WHICH appointment was in the way. A makler
// blocked by her own signed Aufnahme had nothing to act on (support case
// 2026-10-06).
//
// What ends a block differs per light, so the text does too:
//   off    → the planned appointment is moved, or cancelled
//   yellow → it runs out 30 days after its date, or is set to cancelled/rejected
//   green  → nothing ends it — but it refuses only a further Aufnahme
//
// Whoever reads the message is OUTSIDE the case. Since the owner decision of
// 2026-10-06 the gate stops only another makler: the makler of the existing
// appointment and the client's responsible / second makler never see it. So
// every variant closes by saying for whom the appointment CAN be created —
// „only an admin" stopped being true that day.
//
// The gate compares (client, property) pairs and "no property" is a pair of
// its own — hence the last hint when the blocking appointment has none.

import { formatDateTime } from './formatLocale';
import { toMs } from './appointmentDate';

/** `trafficLightBlock` from the dry-run, as it arrives (snake_case). Everything
 *  beyond id and light is optional so an API one deploy behind still yields a
 *  sentence rather than „undefined". */
export interface TrafficLightBlockInfo {
  existing_id: string;
  existing_light: 'off' | 'yellow' | 'green' | 'red' | 'none';
  existing_date?: number | string | null;
  existing_type?: string | null;
  existing_status?: string | null;
  existing_agent_id?: string | null;
  existing_agent_name?: string | null;
  existing_property_id?: string | null;
  existing_property_address?: string | null;
  /** When the block ends by itself; null = never (green). */
  expires_at?: number | string | null;
  other_blocking_count?: number | null;
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

function stamp(value: number | string | null | undefined): string | null {
  const ms = toMs(value ?? null);
  return Number.isFinite(ms) ? formatDateTime(ms) : null;
}

/** „Aufnahme, 24.08.2026, 15:00 · Knöllgasse 38, 1100 Wien · Susanne Kirsch" —
 *  data joined by a separator, so it needs no translation of its own. */
function describeAppointment(block: TrafficLightBlockInfo, t: Translate): string {
  const type = block.existing_type
    ? t(`enums.appointment_type.${block.existing_type}`, { defaultValue: block.existing_type })
    : null;
  const when = stamp(block.existing_date);
  // Without a date the API predates the descriptor: say nothing about the
  // object rather than claim „ohne Immobilie" on a guess.
  const known = when != null || type != null;
  const object = !known
    ? null
    : block.existing_property_id
      ? (block.existing_property_address ?? null)
      : t('traffic_light.blocked_no_object', { defaultValue: 'ohne Immobilie' });
  const parts = [[type, when].filter(Boolean).join(', '), object, block.existing_agent_name ?? null];
  const described = parts.filter(Boolean).join(' · ');
  // Last resort: the light's own label, so the parentheses are never empty.
  return described || t(`traffic_light.${block.existing_light}`, { defaultValue: block.existing_light });
}

export function trafficLightBlockMessage(block: TrafficLightBlockInfo, t: Translate): string {
  const appointment = describeAppointment(block, t);
  const cancelled = t('enums.appointment_status.cancelled', { defaultValue: 'Storniert' });
  const rejected = t('enums.appointment_status.rejected', { defaultValue: 'Abgelehnt' });
  const until = stamp(block.expires_at);

  const sentences: string[] = [];
  if (block.existing_light === 'green') {
    sentences.push(
      t('traffic_light.blocked_green', {
        defaultValue:
          'Weiterer Termin der Art „{{aufnahme}}" nicht möglich: Mit dem Termin ({{appointment}}) ist der Auftrag bereits gewonnen — Status „{{signed}}". Diese Sperre läuft nicht ab; andere Terminarten sind davon nicht betroffen.',
        appointment,
        aufnahme: t('enums.appointment_type.aufnahme', { defaultValue: 'Aufnahme' }),
        signed: t('enums.appointment_status.signed_contract', {
          defaultValue: 'Vertrag unterschrieben',
        }),
      }),
    );
  } else if (block.existing_light === 'yellow' && until) {
    sentences.push(
      t('traffic_light.blocked_yellow', {
        defaultValue:
          'Neuer Termin nicht möglich: Der Termin ({{appointment}}) liegt höchstens 30 Tage zurück. Für andere Makler ist ein neuer ab {{until}} möglich — oder sofort, wenn dieser Termin auf „{{cancelled}}" oder „{{rejected}}" gesetzt wird.',
        appointment,
        until,
        cancelled,
        rejected,
      }),
    );
  } else {
    // A planned appointment — and a recent one whose end date did not arrive:
    // „moved, cancelled, or 30 days old" is true for both.
    sentences.push(
      t('traffic_light.blocked_off', {
        defaultValue:
          'Neuer Termin nicht möglich: Für diesen Klienten gibt es bereits einen Termin ({{appointment}}). Statt eines zweiten kann dieser verschoben werden. Für andere Makler ist ein neuer erst möglich, wenn dieser auf „{{cancelled}}" gesetzt wurde oder 30 Tage zurückliegt.',
        appointment,
        cancelled,
      }),
    );
  }

  const others = block.other_blocking_count ?? 0;
  if (others > 0) {
    sentences.push(
      t('traffic_light.blocked_more', {
        defaultValue: 'Weitere Termine, die ebenfalls sperren: {{count}}.',
        count: others,
      }),
    );
  }

  sentences.push(
    t('traffic_light.blocked_who', {
      defaultValue:
        'Für den Makler dieses Termins und für den zuständigen bzw. zweiten Makler des Klienten gilt die Sperre nicht. Sonst kann den Termin nur ein Admin anlegen.',
    }),
  );

  // Only when we KNOW the blocker has no object (the field arrived as null).
  if (block.existing_property_id === null) {
    sentences.push(
      t('traffic_light.blocked_hint_object', {
        defaultValue:
          'Geht es beim neuen Termin um eine bestimmte Immobilie, wählen Sie sie hier im Formular aus.',
      }),
    );
  }

  return sentences.join(' ');
}
